import { createContext, useContext, useState, useEffect, useCallback } from "react";
import { api } from "@/lib/api";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(() => {
    try {
      return JSON.parse(localStorage.getItem("eleven_user") || "null");
    } catch (e) {
      return null;
    }
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const token = localStorage.getItem("eleven_token");
    if (!token) {
      setLoading(false);
      return;
    }
    api
      .get("/auth/me")
      .then((res) => {
        setUser(res.data);
        localStorage.setItem("eleven_user", JSON.stringify(res.data));
      })
      .catch(() => {
        localStorage.removeItem("eleven_token");
        localStorage.removeItem("eleven_user");
        setUser(null);
      })
      .finally(() => setLoading(false));
  }, []);

  const login = useCallback(async (email, password) => {
    const res = await api.post("/auth/login", { email, password });
    // 2FA aktiv: kein Token, erst Code-Schritt abschliessen
    if (res.data.requires_2fa) {
      return { requires2FA: true, preAuthToken: res.data.pre_auth_token };
    }
    // Admin hat 2FA vorgeschrieben: Enroll-Token speichern (erlaubt NUR die
    // 2FA-Einrichtung), Nutzer wird zum gefuehrten Setup geleitet
    if (res.data.must_setup_2fa) {
      localStorage.setItem("eleven_token", res.data.enroll_token);
      return { mustSetup2FA: true };
    }
    // Passwortwechsel nach erstem Login vorgeschrieben
    if (res.data.must_change_password) {
      localStorage.setItem("eleven_token", res.data.pwchange_token);
      return { mustChangePassword: true };
    }
    localStorage.setItem("eleven_token", res.data.access_token);
    localStorage.setItem("eleven_user", JSON.stringify(res.data.user));
    setUser(res.data.user);
    return { user: res.data.user };
  }, []);

  const complete2FA = useCallback(async (preAuthToken, code) => {
    const res = await api.post("/auth/login/2fa", { pre_auth_token: preAuthToken, code });
    // Auch nach 2FA kann der erzwungene Passwortwechsel greifen
    if (res.data.must_change_password) {
      localStorage.setItem("eleven_token", res.data.pwchange_token);
      return { mustChangePassword: true };
    }
    localStorage.setItem("eleven_token", res.data.access_token);
    localStorage.setItem("eleven_user", JSON.stringify(res.data.user));
    setUser(res.data.user);
    return { user: res.data.user };
  }, []);

  const logout = useCallback(() => {
    localStorage.removeItem("eleven_token");
    localStorage.removeItem("eleven_user");
    setUser(null);
  }, []);

  return <AuthContext.Provider value={{ user, setUser, login, complete2FA, logout, loading }}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
}

export const ROLE_LABELS = {
  super_admin: { de: "Super Admin", en: "Super Admin" },
  admin: { de: "Admin", en: "Admin" },
  manager: { de: "Manager", en: "Manager" },
  berater: { de: "Berater", en: "Advisor" },
  trainee: { de: "Trainee", en: "Trainee" },
  compliance: { de: "Compliance", en: "Compliance" },
  backoffice: { de: "Backoffice", en: "Back Office" },
};
